// Ported from dev-scrips-pm-react src/patients/components/billing/patient-invoice-row-actions.tsx (PROD-1119 Patients UI port).
/**
 * PatientInvoiceRowActions — per-row kebab for the Patient Billing → Invoices
 * sub-tab table. Ports Flutter `patient_invoices_list_screen.dart`
 * `menuOptions()` (:461-511).
 *
 * Menu shape (Flutter):
 *   Issued rows, in order:
 *     1. Add Payment      → opens the invoice add-payment popup
 *     2. View invoice     → opens the invoice detail
 *   Every other status → ONLY "View invoice".
 *
 * "View invoice" navigates to the billing-module invoice route
 * (`/billing/invoice/:invoiceId`) rather than importing the billing
 * `InvoiceDetailPage` — the `patients/` domain must not cross-import
 * `billing/` (CLAUDE.md domain rule). Mirrors how the Appointments tab
 * kebab routes to `/scheduling/appointment/:id` (PR #413).
 *
 * "Add Payment" reuses the shared billing-module `AddPaymentDialog`
 * (the same `AddPaymentForm` used inline on View Claim + appointment→billing:
 * Cash/Card/Bank/Balance + Pay-in-full/Other, invoice-scoped `useAddPayment`).
 * On success it invalidates the patient-billing caches that hook doesn't touch.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Eye, MoreHorizontal, Plus, Printer, type LucideIcon } from 'lucide-react';
import { RowActionsTrigger } from '../row-actions-trigger';
import { AnchoredPopover, AnchoredPopoverContent } from '@scripsteam/scrips-signal-ds';
import { AddPaymentDialog } from '@/billing/components/add-payment-dialog';
// PA-only: PM ships no invoice print path. Drives the "Print invoice" item below.
import { usePrintInvoice } from '@/billing/services/use-invoice-actions';
// Shared invoice-status constant (Flutter `app_constants.dart:733`
// `Invoice_status_ISSUED = "Issued"`) — single source of truth so a
// master-data value change only needs one edit.
import { INVOICE_STATUS_ISSUED } from '@/billing/lib/billing-constants';
import type { InvoiceMainModel } from '@/shared/types/billing';

export interface PatientInvoiceRowActionsProps {
  row: InvoiceMainModel;
  patientId: string;
}

export function PatientInvoiceRowActions({
  row,
  patientId,
}: PatientInvoiceRowActionsProps) {
  // PROD-2743 · switch ON: the DS `more` glyph, as the patients list row actions (#548). OFF = today.
  const navigate = useNavigate();
  // PA-only: the shipped billing print path (see the header note).
  const printInvoice = usePrintInvoice();
  const qc = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);
  const [addPaymentOpen, setAddPaymentOpen] = useState(false);

  const isIssued = row.invoiceStatus === INVOICE_STATUS_ISSUED;
  const invoiceId = row.id;

  const open = (fn: () => void) => () => {
    setMenuOpen(false);
    fn();
  };

  const refreshPatientBilling = () => {
    void qc.invalidateQueries({ queryKey: ['patient-invoices', patientId] });
    void qc.invalidateQueries({
      queryKey: ['patient-available-balance', patientId],
    });
  };

  return (
    <>
      <div className="flex items-center justify-end">
        <AnchoredPopover open={menuOpen} onOpenChange={setMenuOpen}>
          <RowActionsTrigger ariaLabel="Invoice actions" tooltip={`Actions for invoice ${row.invoiceNumber ?? ''}`.trim()}>
            <MoreHorizontal className="size-4" aria-hidden />
          </RowActionsTrigger>
          <AnchoredPopoverContent collisionAvoidance={{ fallbackAxisSide: 'end' }} align="end" side="bottom" className="w-48 p-1 gap-3">
            {isIssued ? (
              <MenuItem
                icon={Plus}
                label="Add Payment"
                onClick={open(() => setAddPaymentOpen(true))}
              />
            ) : null}
            <MenuItem
              icon={Eye}
              label="View invoice"
              onClick={open(() => {
                if (invoiceId) navigate(`/billing/invoice/${invoiceId}`);
              })}
            />
            {/* PA-ONLY — PM has no invoice print. Deleting this is the exact
                regression a wholesale copy of PM's file caused twice
                (PROD-1817); `patient-invoice-row-actions.test.tsx` guards it. */}
            <MenuItem
              icon={Printer}
              label="Print invoice"
              onClick={open(() => {
                if (invoiceId) printInvoice.mutate(invoiceId);
              })}
            />
          </AnchoredPopoverContent>
        </AnchoredPopover>
      </div>

      {invoiceId && (
        <AddPaymentDialog
          open={addPaymentOpen}
          onOpenChange={setAddPaymentOpen}
          invoiceId={invoiceId}
          // Row context: "Pay in full" pays the remaining outstanding.
          patientPayable={row.patientOutstanding ?? 0}
          patientOutstanding={row.patientOutstanding ?? undefined}
          onSuccess={refreshPatientBilling}
        />
      )}
    </>
  );
}

function MenuItem({
  icon: Glyph,
  label,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded px-3 py-1.5 text-left text-sm text-text-primary hover:bg-grey-100"
    >
      <Glyph className="size-4" aria-hidden />
      <span>{label}</span>
    </button>
  );
}
